/**
 * Kategori detayları — işlem ürünü → fatura satırı eşlemesi.
 * Değer anlamları:
 * - Sarrafiye sayı (>100): has çarpanı = değer/1000 (1605 → 1.605)
 * - Sarrafiye sayı (≤100): has çarpanı = değer (16.06, 6.61)
 * - Gram/Takı sayı: milyem/1000 (916 → 0.916)
 * - "8 AYAR": faturada 8 Ayar Takı (milyem 333)
 * - Boş: tam gram → 24 Ayar Gram Altın; küsurat → 22 Ayar Bilezik
 */

export const INVOICE_CATALOG_CATEGORIES = ['Sarrafiye', 'Gram Altın', 'Takı Ürün'];

export const BILEZIK_22_INVOICE_NAME = '22 Ayar Bilezik';
export const TAKI_8_AYAR_INVOICE_NAME = '8 Ayar Takı';

const RAW_ROWS = [
  // Sarrafiye
  ['Sarrafiye', 'Eski Ziynet Çeyrek', '1605'],
  ['Sarrafiye', 'Yeni Ziynet Çeyrek', '1635'],
  ['Sarrafiye', 'Eski Ziynet Yarım', '3210'],
  ['Sarrafiye', 'Yeni Ziynet Yarım', '3270'],
  ['Sarrafiye', 'Eski Ziynet Tam', '6420'],
  ['Sarrafiye', 'Yeni Ziynet Tam', '6520'],
  ['Sarrafiye', "Eski Ziynet 2.5'lu Gremse", '16.0600'],
  ['Sarrafiye', "Yeni Ziynet 2.5'lu Gremse", '16.2200'],
  ['Sarrafiye', 'Eski Ziynet Beşli', ''],
  ['Sarrafiye', 'Yeni Ziynet Beşli', ''],
  ['Sarrafiye', 'Cumhuriyet Ata Çeyrek', ''],
  ['Sarrafiye', 'Cumhuriyet Ata Yarım', ''],
  ['Sarrafiye', 'Cumhuriyet Ata', '6.610'],
  ['Sarrafiye', "Cumhuriyet Ata 2.5'lu", ''],
  ['Sarrafiye', 'Cumhuriyet Ata Beşli', ''],
  ['Sarrafiye', 'Reşat Çeyrek', ''],
  ['Sarrafiye', 'Reşat Yarım', ''],
  ['Sarrafiye', 'Reşat', ''],
  ['Sarrafiye', "Reşat 2.5'lu", ''],
  ['Sarrafiye', 'Reşat Beşli', ''],
  // Gram Altın
  ['Gram Altın', '1 Gram 22 Ayar', '916'],
  ['Gram Altın', '0.50 Gram 22 Ayar', '8 AYAR'],
  ['Gram Altın', '0.25 Gram 22 Ayar', '8 AYAR'],
  ['Gram Altın', '1 Gram 24 Ayar', '995'],
  ['Gram Altın', '0.50 Gram 24 Ayar', '8 AYAR'],
  ['Gram Altın', '0.25 Gram 24 Ayar', '8 AYAR'],
  ['Gram Altın', '2.50 Gram 24 Ayar', '995'],
  ['Gram Altın', '5 Gram 24 Ayar', '995'],
  ['Gram Altın', '10 Gram 24 Ayar', '995'],
  ['Gram Altın', '20 Gram 24 Ayar', '995'],
  ['Gram Altın', '50 Gram 24 Ayar', '995'],
  ['Gram Altın', '100 Gram 24 Ayar', '995'],
  // Takı Ürün
  ['Takı Ürün', '22 Ayar Bilezik', '916'],
  ['Takı Ürün', '14 Ayar Bilezik', '585'],
  ['Takı Ürün', '8 Ayar Bilezik', '333'],
  ['Takı Ürün', '22 Ayar Takı', '916'],
  ['Takı Ürün', '21 Ayar Takı', '875'],
  ['Takı Ürün', '18 Ayar Takı', '750'],
  ['Takı Ürün', '14 Ayar Takı', '585'],
  ['Takı Ürün', '8 Ayar Takı', '333']
];

export const normalizeCatalogKey = (value) =>
  String(value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/[''`´]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

const slugify = (category, productName) =>
  `${normalizeCatalogKey(category)}__${normalizeCatalogKey(productName)}`.replace(/[^a-z0-9şğüçıöİ_-]+/gi, '-');

/** Ham tablo değerini satır alanlarına çevirir. */
export const parseCatalogValue = (category, rawValue) => {
  const raw = String(rawValue ?? '').trim();
  const cat = String(category || '').trim();

  if (!raw) {
    return { valueRaw: '', mode: 'empty', hasFactor: null, milyem: null, invoiceAs: null };
  }

  if (/^8\s*ayar$/i.test(raw)) {
    return {
      valueRaw: '8 AYAR',
      mode: 'invoiceAs',
      hasFactor: null,
      milyem: 0.333,
      invoiceAs: TAKI_8_AYAR_INVOICE_NAME
    };
  }

  const num = Number(String(raw).replace(',', '.'));
  if (!Number.isFinite(num) || num <= 0) {
    return { valueRaw: raw, mode: 'empty', hasFactor: null, milyem: null, invoiceAs: null };
  }

  if (cat === 'Sarrafiye') {
    const hasFactor = num > 100 ? num / 1000 : num;
    return { valueRaw: raw, mode: 'hasFactor', hasFactor, milyem: null, invoiceAs: null };
  }

  // Gram Altın / Takı Ürün — milyem
  const milyem = num > 1 ? num / 1000 : num;
  return { valueRaw: raw, mode: 'milyem', hasFactor: null, milyem, invoiceAs: null };
};

export const buildCatalogRow = (category, productName, valueRaw = '') => {
  const parsed = parseCatalogValue(category, valueRaw);
  return {
    id: slugify(category, productName),
    category: String(category || '').trim(),
    productName: String(productName || '').trim(),
    ...parsed
  };
};

export const DEFAULT_INVOICE_PRODUCT_CATALOG = RAW_ROWS.map(([category, productName, valueRaw]) =>
  buildCatalogRow(category, productName, valueRaw)
);

export const mergeInvoiceProductCatalog = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return DEFAULT_INVOICE_PRODUCT_CATALOG.map((r) => ({ ...r }));
  }
  return rows
    .map((row) => {
      const category = String(row?.category || '').trim();
      const productName = String(row?.productName || '').trim();
      if (!category || !productName) return null;
      const valueRaw =
        row?.valueRaw != null && String(row.valueRaw).trim() !== ''
          ? row.valueRaw
          : row?.mode === 'invoiceAs'
            ? '8 AYAR'
            : row?.hasFactor != null
              ? String(row.hasFactor)
              : row?.milyem != null
                ? String(Math.round(Number(row.milyem) * 1000))
                : '';
      return buildCatalogRow(category, productName, valueRaw);
    })
    .filter(Boolean);
};

/** Ürün adından birim gram (ör. "5 Gram 24 Ayar" → 5). */
export const parseUnitGramsFromProductName = (productName) => {
  const m = String(productName || '').match(/(\d+(?:[.,]\d+)?)\s*gram/i);
  if (!m) return 0;
  const n = Number(String(m[1]).replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const isWholeNumberGrams = (grams) => {
  const n = Number(grams);
  if (!Number.isFinite(n) || n <= 0) return false;
  return Math.abs(n - Math.round(n)) < 1e-6;
};

/**
 * İşlem ürününü katalog satırıyla eşler.
 * Önce kategori+ürün, sonra yalnız ürün adı.
 */
export const findCatalogRowForIslem = (islem, catalog = DEFAULT_INVOICE_PRODUCT_CATALOG) => {
  const rows = mergeInvoiceProductCatalog(catalog);
  const urunKey = normalizeCatalogKey(islem?.urun);
  const katKey = normalizeCatalogKey(islem?.kategori);
  if (!urunKey) return null;

  const exact = rows.find(
    (r) => normalizeCatalogKey(r.productName) === urunKey && (!katKey || normalizeCatalogKey(r.category) === katKey)
  );
  if (exact) return exact;

  const byName = rows.find((r) => normalizeCatalogKey(r.productName) === urunKey);
  if (byName) return byName;

  // "Eski Ziynet Çeyrek Altın" gibi uzatılmış adlar
  const loose = rows.find((r) => {
    const pk = normalizeCatalogKey(r.productName);
    return urunKey.includes(pk) || pk.includes(urunKey);
  });
  return loose || null;
};

/**
 * Faturadaki ad → katalogdaki satır adı. Faturada "24 Ayar Gram Altın" yazıyor,
 * katalogda ise "1 Gram 24 Ayar"; eşleşmeyince Ayarlar'daki değer (ör. 1000)
 * hiç okunmuyor, sabit 0,995 kullanılıyordu (02.10.2026).
 */
const INVOICE_NAME_CATALOG_ALIAS = {
  [normalizeCatalogKey('24 Ayar Gram Altın')]: normalizeCatalogKey('1 Gram 24 Ayar'),
  [normalizeCatalogKey('22 Ayar Gram Altın')]: normalizeCatalogKey('1 Gram 22 Ayar')
};

export const resolveMilyemForInvoiceName = (invoiceName, catalog = DEFAULT_INVOICE_PRODUCT_CATALOG) => {
  const rows = mergeInvoiceProductCatalog(catalog);
  const key = normalizeCatalogKey(invoiceName);
  const alias = INVOICE_NAME_CATALOG_ALIAS[key];
  const row =
    rows.find((r) => normalizeCatalogKey(r.productName) === key && r.milyem != null) ||
    (alias ? rows.find((r) => normalizeCatalogKey(r.productName) === alias && r.mode === 'milyem' && r.milyem != null) : null);
  if (row?.milyem) return Number(row.milyem);
  if (/24\s*ayar/i.test(String(invoiceName || ''))) return 0.995;
  if (/22\s*ayar/i.test(String(invoiceName || ''))) return 0.916;
  if (/8\s*ayar/i.test(String(invoiceName || ''))) return 0.333;
  if (/14\s*ayar/i.test(String(invoiceName || ''))) return 0.585;
  if (/18\s*ayar/i.test(String(invoiceName || ''))) return 0.75;
  if (/21\s*ayar/i.test(String(invoiceName || ''))) return 0.875;
  return null;
};
